import React, { useContext, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import LaunchIcon from "@mui/icons-material/Launch";
import ActionsContext from "common/ActionsContext";
import { Header } from "common/Header";
import LazyInView from "common/LazyInView";
import BqnContext from "common/BqnContext";
import FilterContext from "common/FilterContext";
import { retrieveRange } from "common/api";
import useProfile from "common/hooks/useProfile";
import Alarms from "./Alarms";
import SystemInformation from "./SystemInformation";
import PeriodSelector, { periods } from "./PeriodSelector";
import ThroughputValues from "./ThroughputValues";
import {
  LOAD_INTERVAL,
  LOAD_DYNAMICS_INTERVAL,
  LOAD_STATUS_INTERVAL,
  PERIOD_DASHBOARD_TO_HOURS,
  normalizeUnits,
  getDynamics,
  getStatus,
  percentOver,
  getDbTCPOpt,
  getdbPps,
  formatLargeStrNum,
  RETURN_VIEW,
  DYNAMICS_INTERVAL_RETRY,
  pollWhileShown,
  valueAndUnit,
} from "./utils";
import Throughput from "./Throughput";
import Latency from "./Latency";
import Retransmissions from "./Retransmissions";
import InternetSideLatency from "./InternetSideLatency";
import ActiveFunctionalities from "./ActiveFunctionalities";
import Subscribers from "./Subscribers";
import SubscriberGroups from "./SubscriberGroups";
import MemoryUsage from "./MemoryUsage";
import ApplicationMetrics from "./ApplicationMetrics";
import ApplicationMetricsTime from "./ApplicationMetricsTime";
import TCPOComparison from "./TCPOComparison";
import ActiveFlows from "./ActiveFlows";
import CPUUsage from "./CPUUsage";
import PoliciesThroughput from "./PoliciesThroughput";
import SubscribersPerPolicy from "./SubscribersPerPolicy";
import RulesTreeView from "./RulesTreeView";
import MarkdownHelpModal from "common/MarkdownHelpModal";
import help from "help/viewDashboard.md";
import DPIAnalytics from "./DPIAnalytics";
import SubscriberDashboardSearch from "./SubscriberDashboardSearch";

// The handler of an error of a retrieval, which tells whether to keep asking:
// a BQN that has gone offline answers nothing, so the whole cluster is
// selected instead and the retrievals stop, since the view changes with it.
const handleRetrievalError = (message, setBqnId) => (error) => {
  console.error(message, error);

  // The CLI reports its errors as strings, but a parser of a response that
  // does not carry what it expects throws an Error: taking it as a string
  // keeps the handler from throwing inside the catch, which would stop the
  // retrievals for good instead of asking again.
  if (String(error).match("%ERR-ENOENT: Cannot execute in offline BQN ID")) {
    setBqnId("all");
    return false;
  }

  return true;
};

const WrapperPage = styled.div`
  padding-bottom: 1.5rem;
`;

const Wrapper = styled.div`
  min-height: 100vh;

  .dashboard-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 1.5rem; /* Standard 24px uniform gutter */
  }

  .card {
    background: #ffffff;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 0px;

    &.align-to-bottom {
      display: flex;
      justify-content: end;
      flex-direction: column;

      p.big-content {
        margin-bottom: 10px;
      }
    }

    .full-height {
      height: 100%;
    }

    h3 {
      font-size: 10px;
      color: #6b7280;
      text-transform: uppercase;
      margin-bottom: 0.5rem;
      margin-top: 0px;

      &.top-row {
        text-transform: none;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        font-size: 12px;
        font-weight: 100;
        margin-top: 5px;

        a {
          font-size: 12px;
          padding-top: 4px;
        }

        svg {
          width: 11px;
          height: 11px;
          margin-left: 5px;
        }
      }

      a {
        font-size: 10px;
        color: inherit;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        transition: color 0.2s;

        svg {
          font-size: 1.1rem;
          margin-left: 0.5rem;
          margin-top: -3px;
        }

        &:hover {
          color: var(--text-primary, #000);
        }
      }
    }

    p.big-content {
      text-align: right;
      font-family: "IBM Plex Mono", monospace !important;
      font-size: 28px;
      color: var(--text-primary) !important;
      margin-bottom: 0;

      span {
        font-size: 1.5rem;
      }
    }

    &.click {
      &:hover {
        cursor: pointer;
      }
    }

    &.span-2 {
      grid-column: span 2;
    }

    &.span-3 {
      grid-column: span 3;
    }

    &.span-4 {
      grid-column: span 4;
    }

    &.span-5 {
      grid-column: span 5;
    }

    &.span-6 {
      grid-column: span 6;
    }

    &.span-8 {
      grid-column: span 8;
    }

    &.span-12 {
      grid-column: span 12;
    }
  }

  @media (max-width: 1024px) {
    .card.span-4,
    .card.span-5,
    .card.span-3 {
      grid-column: span 6;
    }
    .card.span-8 {
      grid-column: span 12;
    }
    .card.span-2 {
      grid-column: span 4;
    }
    .card.span-4.throughput {
      grid-column: span 8;
    }
  }

  @media (max-width: 768px) {
    .card.span-3,
    .card.span-4,
    .card.span-5,
    .card.span-6,
    .card.span-8,
    .card.span-12 {
      grid-column: span 12;
    }
  }

  @media (max-width: 550px) {
    .card.span-4.throughput {
      grid-column: span 12;
    }
    .card.span-2 {
      grid-column: span 6;
    }
  }
`;

const TwoNumberWrapper = styled.div`
  width: 100%;
  margin-top: 6px;
  margin-bottom: 12px;
`;

const TwoNumber = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  margin-bottom: 5px;

  &:hover {
    cursor: pointer;
  }

  .value {
    text-align: right;
    font-family: "IBM Plex Mono", monospace !important;
    font-size: 16px;
    color: var(--text-primary) !important;
    display: flex;
    align-items: baseline;
    span {
      display: block;
      width: 30px;
      text-align: left;
      font-size: 12px;
      margin-left: 4px;
      color: #6b7280;
      align-items: baseline;
    }
  }

  .labelTwo {
    width: 60px;
    text-align: right;
    color: #6b7280;
    font-size: 12px;
    margin-left: 10px;
  }
`;

const HeaderCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`;

const ContextMenu = ({ period, setPeriod }) => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <PeriodSelector period={period} setPeriod={setPeriod} />
      </li>
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => actions.send("open-help")}
        >
          <i className="material-icons">
            <small>help_outline</small>
          </i>
        </a>
      </li>
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Refresh"
          onClick={() => actions.send("do-load")}
        >
          <i className="material-icons">
            <small>refresh</small>
          </i>
        </a>
      </li>
    </ul>
  );
};

function Dashboard() {
  const {
    setBqnId,
    clusterStatus: { mode },
    bqns,
    bqnId,
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const profile = useProfile();
  const [getSettings, putSettings] = useContext(FilterContext);
  const period = getSettings(RETURN_VIEW)?.period ?? periods[0];
  const setPeriod = (periodChosen) =>
    putSettings(RETURN_VIEW, {
      period: periodChosen,
      hours: PERIOD_DASHBOARD_TO_HOURS[periodChosen],
    });
  const [rangeLoaded, setRangeLoaded] = useState();
  const [alarms, setAlarms] = useState();
  const [dynamics, setDynamics] = useState({});
  const [latencies, setLatencies] = useState();
  const [retransmissions, setRetransmissions] = useState();
  const [systemTime, setSystemTime] = useState();
  const [downUpLinkValues, setDownUpLinkValues] = useState({
    shapingDown: "0 %",
    shapingUp: "0 %",
    rateDown: "0 %",
    rateUp: "0 %",
    dbRateDw: "n/a Mbps",
    dbRateUp: "n/a Mbps",
    dbPpsDwValue: "n/a",
    dbPpsDwUnit: "Kpps",
    dbPpsUpValue: "n/a",
    dbPpsUpUnit: "Kpps",
  });
  const [usage, setUsage] = useState({
    opt: "0 %",
    flows: "0",
  });
  const [status, setStatus] = useState({});
  const dynamicsInfoRef = useRef(null);

  const {
    TotalFlows,
    EpocTime,
    TotalDnTxBytes,
    TotalUpTxBytes,
    ShpAllDnTxBytes,
    ShpAllUpTxBytes,
    RatePerSubsDnTxBytes,
    RatePerSubsUpTxBytes,
    TcpOptDnTxBytes,
    TcpOptUpTxBytes,
    TcpDnTxBytes,
    TcpUpTxBytes,
    TotalDnTxPkt,
    TotalUpTxPkt,
  } = dynamics;
  const { activeGroups, activeSubs } = status;

  // The charts of the dashboard share one range, so the date of the BQN is
  // asked for once and not once per chart. A new range is what reloads them,
  // which makes this the clock of the whole page: it ticks on a change of the
  // period or of the BQN, every LOAD_INTERVAL and on a refresh.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }
    let isMounted = true;

    const loadRange = () =>
      retrieveRange(PERIOD_DASHBOARD_TO_HOURS[period], mode, bqnId)
        .then((rangeRetrieved) => {
          if (isMounted) {
            setRangeLoaded({ period, mode, bqnId, range: rangeRetrieved });
          }
        })
        .catch((error) =>
          console.error("Error fetching the dashboard range:", error),
        );

    loadRange();

    const intervalId = setInterval(loadRange, LOAD_INTERVAL);
    const unsubscribe = actions.recv("do-load", loadRange);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      unsubscribe();
    };
  }, [period, mode, bqnId, actions]);

  // The range is handed to the charts only once it is the one of the period,
  // the mode and the BQN shown: with the one loaded for the ones before, they
  // would load once for it and again for the new one, taking two turns of the
  // queue of the CLI each for one that is drawn. Until then they are loading.
  const range =
    rangeLoaded !== undefined &&
    rangeLoaded.period === period &&
    rangeLoaded.mode === mode &&
    rangeLoaded.bqnId === bqnId
      ? rangeLoaded.range
      : undefined;

  // The live data of the dashboard, whose cadence is the one the throughput
  // and the packet rate are drawn at: what is shown of the statistics is the
  // difference between two answers of the command, which is why the batch
  // runs beside the queue of the CLI instead of taking turns with the cards.
  //
  // Nothing is loaded while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    return pollWhileShown({
      retrieve: () => getDynamics(mode, bqnId),
      take: ({
        statisticsPktEng,
        latencies: latenciesRetrieved,
        retransmissions: retransmissionsRetrieved,
        systemTime: systemTimeRetrieved,
      }) => {
        setDynamics(statisticsPktEng);
        setLatencies(latenciesRetrieved);
        setRetransmissions(retransmissionsRetrieved);
        setSystemTime(systemTimeRetrieved);
      },
      handleError: handleRetrievalError(
        "Error fetching dynamic dashboard data:",
        setBqnId,
      ),
      interval: LOAD_DYNAMICS_INTERVAL,
      intervalRetry: DYNAMICS_INTERVAL_RETRY,
    });
  }, [bqnId, bqns, mode]);

  // The status of the BQN: its alarms, the counts of what it serves and its
  // latency. None of them is a difference between two answers, so they are
  // asked for on a cadence of their own, which takes turns of the queue of
  // the CLI away from the live data above far less often.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    return pollWhileShown({
      retrieve: () => getStatus(mode, bqnId),
      take: ({ alarms: alarmsRetrieved, ...statusRetrieved }) => {
        setAlarms(alarmsRetrieved);
        setStatus(statusRetrieved);
      },
      handleError: handleRetrievalError(
        "Error fetching the dashboard status:",
        setBqnId,
      ),
      interval: LOAD_STATUS_INTERVAL,
    });
  }, [bqnId, bqns, mode]);

  useEffect(() => {
    const totalDnTxBytesN = (TotalDnTxBytes * 8) / 1000000;
    const totalUpTxBytesN = (TotalUpTxBytes * 8) / 1000000;
    const ShpAllDnTxBytesN = (ShpAllDnTxBytes * 8) / 1000000;
    const ShpAllUpTxBytesN = (ShpAllUpTxBytes * 8) / 1000000;
    const RatePerSubsDnTxBytesN = (RatePerSubsDnTxBytes * 8) / 1000000;
    const RatePerSubsUpTxBytesN = (RatePerSubsUpTxBytes * 8) / 1000000;
    const TcpOptDnTxBytesN = (TcpOptDnTxBytes * 8) / 1000000;
    const TcpOptUpTxBytesN = (TcpOptUpTxBytes * 8) / 1000000;
    const TcpDnTxBytesN = (TcpDnTxBytes * 8) / 1000000;
    const TcpUpTxBytesN = (TcpUpTxBytes * 8) / 1000000;
    const TotalDnTxPktN = TotalDnTxPkt;
    const TotalUpTxPktN = TotalUpTxPkt;
    const totalFlowsN =
      TotalFlows !== undefined
        ? formatLargeStrNum(TotalFlows.toString(), 0)
        : "unknown";

    const newUsage = {
      opt: "0 %",
      flows: totalFlowsN,
    };

    if (
      dynamicsInfoRef.current &&
      dynamicsInfoRef.current.EpocTime &&
      EpocTime - dynamicsInfoRef.current.EpocTime !== 0
    ) {
      const elapsed = (EpocTime - dynamicsInfoRef.current.EpocTime) / 1000;
      const {
        totalDnTxBytes: totalDnTxBytesCurrent,
        totalUpTxBytes: totalUpTxBytesCurrent,
        ShpAllDnTxBytes: ShpAllDnTxBytesCurrent,
        ShpAllUpTxBytes: ShpAllUpTxBytesCurrent,
        RatePerSubsDnTxBytes: RatePerSubsDnTxBytesCurrent,
        RatePerSubsUpTxBytes: RatePerSubsUpTxBytesCurrent,
        TcpOptDnTxBytes: TcpOptDnTxBytesCurrent,
        TcpOptUpTxBytes: TcpOptUpTxBytesCurrent,
        TcpDnTxBytes: TcpDnTxBytesCurrent,
        TcpUpTxBytes: TcpUpTxBytesCurrent,
        TotalDnTxPkt: TotalDnTxPktCurrent,
        TotalUpTxPkt: TotalUpTxPktCurrent,
      } = dynamicsInfoRef.current;
      const dbTCPOpt = getDbTCPOpt(
        TcpDnTxBytesN,
        TcpDnTxBytesCurrent,
        TcpUpTxBytesN,
        TcpUpTxBytesCurrent,
        TcpOptDnTxBytesN,
        TcpOptDnTxBytesCurrent,
        TcpOptUpTxBytesN,
        TcpOptUpTxBytesCurrent,
      );
      newUsage.opt = dbTCPOpt;
      const { dbPpsValue: dbPpsDwValue, dbPpsUnit: dbPpsDwUnit } = getdbPps(
        TotalDnTxPktN,
        TotalDnTxPktCurrent,
        elapsed,
      );
      const { dbPpsValue: dbPpsUpValue, dbPpsUnit: dbPpsUpUnit } = getdbPps(
        TotalUpTxPktN,
        TotalUpTxPktCurrent,
        elapsed,
      );
      const dbDownTxShapProgress = percentOver(
        ShpAllDnTxBytesN - ShpAllDnTxBytesCurrent,
        totalDnTxBytesN - totalDnTxBytesCurrent,
      );
      const dbUpTxShapProgress = percentOver(
        ShpAllUpTxBytesN - ShpAllUpTxBytesCurrent,
        totalUpTxBytesN - totalUpTxBytesCurrent,
      );
      const dbDownTxRateProgress = percentOver(
        RatePerSubsDnTxBytesN - RatePerSubsDnTxBytesCurrent,
        totalDnTxBytesN - totalDnTxBytesCurrent,
      );
      const dbUpTxRateProgress = percentOver(
        RatePerSubsUpTxBytesN - RatePerSubsUpTxBytesCurrent,
        totalUpTxBytesN - totalUpTxBytesCurrent,
      );
      const dbRateDw = normalizeUnits(
        ((totalDnTxBytesN - totalDnTxBytesCurrent) / elapsed).toString(),
        "Mbps",
        3,
      );
      const dbRateUp = normalizeUnits(
        ((totalUpTxBytesN - totalUpTxBytesCurrent) / elapsed).toString(),
        "Mbps",
        3,
      );
      setDownUpLinkValues({
        shapingDown: dbDownTxShapProgress,
        shapingUp: dbUpTxShapProgress,
        rateDown: dbDownTxRateProgress,
        rateUp: dbUpTxRateProgress,
        dbRateDw,
        dbRateUp,
        dbPpsDwValue,
        dbPpsDwUnit,
        dbPpsUpValue,
        dbPpsUpUnit,
      });
    }
    setUsage(newUsage);

    dynamicsInfoRef.current = {
      EpocTime,
      totalDnTxBytes: totalDnTxBytesN,
      totalUpTxBytes: totalUpTxBytesN,
      ShpAllDnTxBytes: ShpAllDnTxBytesN,
      ShpAllUpTxBytes: ShpAllUpTxBytesN,
      RatePerSubsDnTxBytes: RatePerSubsDnTxBytesN,
      RatePerSubsUpTxBytes: RatePerSubsUpTxBytesN,
      TcpOptDnTxBytes: TcpOptDnTxBytesN,
      TcpOptUpTxBytes: TcpOptUpTxBytesN,
      TcpDnTxBytes: TcpDnTxBytesN,
      TcpUpTxBytes: TcpUpTxBytesN,
      TotalDnTxPkt: TotalDnTxPktN,
      TotalUpTxPkt: TotalUpTxPktN,
    };
  }, [
    TotalFlows,
    EpocTime,
    TotalDnTxBytes,
    TotalUpTxBytes,
    ShpAllDnTxBytes,
    ShpAllUpTxBytes,
    RatePerSubsDnTxBytes,
    RatePerSubsUpTxBytes,
    TcpOptDnTxBytes,
    TcpOptUpTxBytes,
    TcpDnTxBytes,
    TcpUpTxBytes,
    TotalDnTxPkt,
    TotalUpTxPkt,
  ]);

  const {
    shapingDown,
    shapingUp,
    rateDown,
    rateUp,
    dbRateDw,
    dbRateUp,
    dbPpsDwValue,
    dbPpsDwUnit,
    dbPpsUpValue,
    dbPpsUpUnit,
  } = downUpLinkValues;
  // The counts are not known until the status of the BQN is retrieved.
  const subscribersShown = activeSubs === undefined ? "unknown" : activeSubs;
  const groupsShown = activeGroups === undefined ? "unknown" : activeGroups;
  // The card of the access metrics shows the latency of the downlink, which
  // is the one measured from the BQN to the end user, as the view it opens
  // draws it.
  const { value: latencyValue, unit: latencyUnit } = valueAndUnit(
    latencies?.down,
  );
  const { value: rtxValue, unit: rtxUnit } = valueAndUnit(retransmissions);
  const isAlarmLicenseAvailable = alarms?.["license-available"];
  const isAlarmLicenseExpiration = alarms?.["license-expiration"];
  const isSpecAlarms =
    isAlarmLicenseAvailable !== "normal" ||
    isAlarmLicenseExpiration !== "normal";

  const handleNav = (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href");
    if (href && href !== "#" && typeof globalNavigate === "function") {
      const [view, query] = href.split("?");
      const params = query
        ? Object.fromEntries(new URLSearchParams(query))
        : undefined;
      globalNavigate(view, { returnView: RETURN_VIEW, ...params });
    }
  };

  return (
    <WrapperPage id="viewDashboard">
      <Header
        title="Dashboard"
        backButton={mode !== "standalone"}
        returnUrl="/viewClusterDashboard"
        contextMenu={<ContextMenu period={period} setPeriod={setPeriod} />}
        big={true}
      />
      <Wrapper className="container-xl">
        <div className="dashboard-grid">
          <div className="card span-4">
            <ThroughputValues
              downRate={dbRateDw}
              upRate={dbRateUp}
              downPps={dbPpsDwValue}
              upPps={dbPpsUpValue}
              downPpsUnit={dbPpsDwUnit}
              upPpsUnit={dbPpsUpUnit}
            />
          </div>
          <div className="card span-2">
            <TwoNumberWrapper>
              <TwoNumber
                onClick={() =>
                  globalNavigate("viewLatencyStats", {
                    returnView: RETURN_VIEW,
                  })
                }
              >
                <div className="value">
                  {latencyValue}
                  <span>{latencyUnit}</span>
                </div>
                <div className="labelTwo">Latency</div>
              </TwoNumber>
              <TwoNumber
                onClick={() =>
                  globalNavigate("viewRtxStats", {
                    returnView: RETURN_VIEW,
                  })
                }
              >
                <div className="value">
                  {rtxValue}
                  <span>{rtxUnit}</span>
                </div>
                <div className="labelTwo">RTX</div>
              </TwoNumber>
            </TwoNumberWrapper>
            <h3 className="top-row">
              <a href="viewCongestionACM" onClick={handleNav}>
                Access metrics <LaunchIcon />
              </a>
            </h3>
          </div>
          <div
            className="card span-2 click align-to-bottom"
            onClick={() =>
              globalNavigate("viewStatusSubscribers", {
                returnView: RETURN_VIEW,
              })
            }
          >
            <p className="big-content">
              {subscribersShown === "unknown" ? (
                <span>unknown</span>
              ) : (
                subscribersShown
              )}
            </p>
            <h3 className="top-row">
              Subscribers <LaunchIcon />
            </h3>
          </div>
          <div
            className="card span-2 click align-to-bottom"
            onClick={() =>
              globalNavigate("viewStatusSubscriberGroups", {
                returnView: RETURN_VIEW,
              })
            }
          >
            <p className="big-content">
              {groupsShown === "unknown" ? <span>unknown</span> : groupsShown}
            </p>
            <h3 className="top-row">
              Groups <LaunchIcon />
            </h3>
          </div>
          <div
            className="card span-2 click align-to-bottom"
            onClick={() =>
              globalNavigate("viewFlowsPerSubs", {
                returnView: RETURN_VIEW,
              })
            }
          >
            <p className="big-content">
              {usage.flows === "unknown" ? <span>unknown</span> : usage.flows}
            </p>
            <h3 className="top-row">
              Flows <LaunchIcon />
            </h3>
          </div>
          <div className="card span-8">
            <h3>
              <a href="viewCongestionACM" onClick={handleNav}>
                Throughput <LaunchIcon />
              </a>
            </h3>
            <LazyInView className="full-height">
              <Throughput period={period} />
            </LazyInView>
          </div>
          <div className="card span-4">
            <HeaderCard>
              <h3>Alarms</h3>
            </HeaderCard>
            <LazyInView>
              <Alarms data={alarms} />
            </LazyInView>
            <LazyInView>
              <SystemInformation systemTime={systemTime} />
            </LazyInView>
          </div>
          <div className="card span-4">
            <h3>
              <a href="viewStatusTCPO" onClick={handleNav}>
                TCP Speed & Acceleration <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <TCPOComparison
                period={period}
                tcpo={isSpecAlarms ? "0 %" : usage.opt}
              />
            </LazyInView>
          </div>
          <div className="card span-8">
            <h3>
              <a href="viewDPIHourlyVolume" onClick={handleNav}>
                DPI Statistics <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <DPIAnalytics period={period} range={range} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <h3>
              <a href="viewLatencyStats" onClick={handleNav}>
                Latency Over Time <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <Latency period={period} range={range} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <h3>
              <a href="viewRtxStats" onClick={handleNav}>
                Average TCP Retransmissions Over Time <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <Retransmissions period={period} range={range} />
            </LazyInView>
          </div>
          <div className="card span-5">
            <HeaderCard>
              <h3>
                <a href="viewDPILatency" onClick={handleNav}>
                  Internet-side latency per application (ms) <LaunchIcon />
                </a>
              </h3>
            </HeaderCard>
            <LazyInView>
              <InternetSideLatency period={period} />
            </LazyInView>
          </div>
          <div className="card span-4 rules-tree">
            <HeaderCard>
              <h3>
                <a href="viewRulesFlow?tab=0" onClick={handleNav}>
                  Subscribers Flow Configuration
                  <LaunchIcon />
                </a>
              </h3>
            </HeaderCard>
            <LazyInView className="full-height">
              <RulesTreeView />
            </LazyInView>
          </div>
          <div className="card span-3">
            <HeaderCard>
              <h3>Active Functionalities</h3>
            </HeaderCard>
            <LazyInView className="full-height">
              <ActiveFunctionalities parameters={status.parameters} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <HeaderCard>
              <h3>
                <a href="viewThroughputPerPolicy" onClick={handleNav}>
                  Flow Policies Throughput Over Time <LaunchIcon />
                </a>
              </h3>
            </HeaderCard>
            <LazyInView>
              <PoliciesThroughput period={period} type="flow" range={range} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <HeaderCard>
              <h3>
                <a
                  href="viewThroughputPerPolicy?policyType=rate"
                  onClick={handleNav}
                >
                  Rate Policies Throughput Over Time <LaunchIcon />
                </a>
              </h3>
            </HeaderCard>
            <LazyInView>
              <PoliciesThroughput period={period} type="rate" range={range} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <h3>
              <a href="viewBtaStatsSess" onClick={handleNav}>
                Active Flows Over Time <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <ActiveFlows period={period} range={range} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <h3>
              <a href="viewSubscribersPerPolicy" onClick={handleNav}>
                Subscribers per Rate Policy By Time <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <SubscribersPerPolicy period={period} type="rate" range={range} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <h3>
              <a href="viewDPIServiceMetrics" onClick={handleNav}>
                Application Speed Distribution
                <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <ApplicationMetrics period={period} />
            </LazyInView>
          </div>
          <div className="card span-6">
            <h3>
              <a href="viewDPIServiceMetrics" onClick={handleNav}>
                Application Metrics Time Evolution All Apps
                <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <ApplicationMetricsTime period={period} />
            </LazyInView>
          </div>
          <div className="card span-8">
            <h3>
              <a href="viewSysStatsCpu" onClick={handleNav}>
                CPU Usage Over Time <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <CPUUsage period={period} range={range} />
            </LazyInView>
          </div>
          <div className="card span-4">
            <h3>
              <a href="viewSysStatsMem" onClick={handleNav}>
                System Memory Usage Over Time <LaunchIcon />
              </a>
            </h3>
            <LazyInView>
              <MemoryUsage period={period} />
            </LazyInView>
          </div>
          <div className="card span-12">
            <HeaderCard>
              <h3>
                <a href="viewStatusSubscribers" onClick={handleNav}>
                  Subscribers <LaunchIcon />
                </a>
              </h3>
              <SubscriberDashboardSearch period={period} />
            </HeaderCard>
            <LazyInView>
              <Subscribers period={period} profile={profile} />
            </LazyInView>
          </div>
          <div className="card span-12">
            <HeaderCard>
              <h3>
                <a href="viewStatusSubscriberGroups" onClick={handleNav}>
                  Subscriber Groups <LaunchIcon />
                </a>
              </h3>
            </HeaderCard>
            <LazyInView>
              <SubscriberGroups period={period} profile={profile} />
            </LazyInView>
          </div>
        </div>
      </Wrapper>
      <MarkdownHelpModal title="Dashboard" source={help} />
    </WrapperPage>
  );
}

export default Dashboard;
